import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { SortableWrapper } from 'src/components/SortableWrapper';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: 0,
  },
  sortableElement: {
    // set high zIndex to resolve disappearing item while sorting
    // reference: https://github.com/clauderic/react-sortable-hoc#item-disappearing-when-sorting--css-issues
    zIndex: theme.zIndex.modal + 1,
  },
}));

const fixedItemsKeys = ['no-option-item', 'add-new-option-item'];

type OptionElementProps = {
  'data-option-id'?: string;
  children?: React.ReactElement<OptionElementProps> & { key?: string | null };
};

// MultiSelect's customOptionRenderer sets data-option-id on every <li>. We
// prefer it over React keys because MUI Autocomplete may wrap rendered options
// in internal elements, breaking direct key access.
const extractItemKey = (
  child: React.ReactElement<OptionElementProps>,
): string | undefined => {
  const innerChild = child.props?.children;
  return (
    child.props?.['data-option-id'] ??
    innerChild?.props?.['data-option-id'] ??
    (typeof child.key === 'string' ? child.key : undefined) ??
    (innerChild && typeof innerChild.key === 'string'
      ? innerChild.key
      : undefined)
  );
};

type DragHandleProps = {
  attributes: React.HTMLAttributes<HTMLElement>;
  listeners: React.HTMLAttributes<HTMLElement> | undefined;
};

const SortableDragHandleContext = React.createContext<DragHandleProps | null>(
  null,
);

export const useSortableDragHandle = (): DragHandleProps => {
  const ctx = React.useContext(SortableDragHandleContext);
  return ctx ?? { attributes: {}, listeners: undefined };
};

const SortableItem = ({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { classes } = useStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={classes.sortableElement}>
      <SortableDragHandleContext.Provider value={{ attributes, listeners }}>
        {children}
      </SortableDragHandleContext.Provider>
    </div>
  );
};

interface SortableMultiSelectListProps {
  children?: React.ReactNode;
  onSortTagsOptions: (oldIndex: number, newIndex: number) => void;
}

export const SortableMultiSelectList = React.forwardRef<
  HTMLDivElement,
  SortableMultiSelectListProps
>(({ children, onSortTagsOptions, ...others }, ref) => {
  const { classes } = useStyles();

  const childArray = React.Children.toArray(
    children,
  ) as React.ReactElement<OptionElementProps>[];
  const sortableItems: {
    key: string;
    child: React.ReactElement<OptionElementProps>;
    isSortable: boolean;
  }[] = childArray.map((child, index) => {
    const childKey = extractItemKey(child);
    const isSortable = !fixedItemsKeys.includes(childKey ?? '');
    return {
      key: childKey || `item-${index}`,
      child,
      isSortable,
    };
  });

  const sortableIds = sortableItems
    .filter((item) => item.isSortable)
    .map((item) => item.key);

  return (
    <div ref={ref} {...others} className={classes.root}>
      <SortableWrapper
        items={sortableIds}
        onSortEnd={onSortTagsOptions}
        renderOverlay={(activeId) => {
          const active = sortableItems.find((item) => item.key === activeId);
          if (!active) return null;
          return <div className={classes.root}>{active.child}</div>;
        }}
      >
        <div>
          {sortableItems.map((item) =>
            item.isSortable ? (
              <SortableItem key={item.key} id={item.key}>
                {item.child}
              </SortableItem>
            ) : (
              <div key={item.key}>{item.child}</div>
            ),
          )}
        </div>
      </SortableWrapper>
    </div>
  );
});
